<template>
    <div class="flex flex-col border rounded-lgr p-3">
        <BaseTextarea
            v-model:input="model"
            :placeholder="placeholder"
            :rows="rows"
            :disabled="isInputDisabled"
            :aria-label="textareaAriaLabel"
            :minlength="minlength"
            :maxlength="maxlength"
            :class="textareaClass" />
        <button
            v-if="!isTranscribing"
            type="button"
            :disabled="disabled"
            :aria-label="isRecording ? 'Stop recording' : 'Start recording'"
            :aria-pressed="isRecording"
            :title="isRecording ? 'Stop recording' : 'Start recording'"
            class="bi text-2xl/none self-end disabled:text-valence-grey-300"
            :class="[isRecording ? 'bi-stop-circle text-red-500' : 'bi-mic']"
            @click.prevent.stop="handleRecord" />
        <LoadingSpinner v-else class="w-6 h-6 self-end" />
        <slot :is-recording="isRecording" :is-transcribing="isTranscribing" :is-below-silence-threshold="isBelowSilenceThreshold" />
        <slot name="error" :error="error">
            <p v-if="error" class="text-error body-small font-medium">{{ error }}</p>
        </slot>
    </div>
</template>

<script setup lang="ts">
import { AudioRecorder } from "/js/AudioRecorder";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useRecorder } from "~vue/composables/useRecorder";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { computed, onBeforeUnmount, watch } from "vue";

type VoiceTextareaProps = {
    /** Placeholder text shown when the textarea is empty. */
    placeholder?: string;
    /** Number of visible rows (passed through to <textarea>). */
    rows?: number | string;
    /** Disable both the textarea and the mic button. */
    disabled?: boolean;
    /** Accessible label for the textarea. Required for a11y. */
    textareaAriaLabel: string;
    /** HTML `minlength` attribute on the textarea. */
    minlength?: number;
    /** HTML `maxlength` attribute on the textarea. */
    maxlength?: number;
    /** append new recording to existing text */
    appendMode?: boolean;
    /** Extra classes forwarded to the inner BaseTextarea (not the wrapper). */
    textareaClass?: string;
};

const { placeholder = "Type or speak here", rows = 4, disabled = false, minlength, maxlength, appendMode = true, textareaClass } = defineProps<VoiceTextareaProps>();

const emit = defineEmits<{
    "record-start": [];
    "record-end": [];
    "permission-open": [];
    "permission-close": [];
    "update:busy": [boolean];
    error: [string];
}>();

const model = defineModel<string>("input", { default: "" });

const recorder = new AudioRecorder();

const { error, isTranscribing, isRecording, isBelowSilenceThreshold, input: transcript, handleRecord, isAwaitingPermission } = useRecorder({ recorder });

const isInputDisabled = computed(() => disabled || isRecording.value || isTranscribing.value);

watch(transcript, (newTranscript) => {
    if (!newTranscript) {
        return;
    }
    if (appendMode) {
        const existing = model.value?.trim() ?? "";
        model.value = existing ? `${existing} ${newTranscript}` : newTranscript;
    } else {
        model.value = newTranscript;
    }
});

const isBusy = computed(() => isRecording.value || isTranscribing.value);
watch(isBusy, (busy) => emit("update:busy", busy), { immediate: true });

watch(isRecording, (recording) => (recording ? emit("record-start") : emit("record-end")));

watch(error, (errorMessage) => errorMessage && emit("error", errorMessage));

watch(isAwaitingPermission, (awaiting) => (awaiting ? emit("permission-open") : emit("permission-close")));

onBeforeUnmount(() => {
    if (recorder.isRecording) {
        recorder.stop();
    }
    recorder.releaseStream();
});
</script>
